import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, ClipboardList, Check, HelpCircle,
  Sparkles, Shield, ChevronDown, Scale,
} from 'lucide-react';
import api from '../lib/api';
import ScoreGauge from '../components/ScoreGauge';
import { LogoFull } from '../components/Logo';
import { withTermTooltips } from '../components/TermTooltip';
import { SCORE_LEVELS } from '../lib/constants';

/**
 * Diagnóstico gratis (sin login) — flujo de captación desde landing.
 * Pasos: 0 = intro (sinceridad) · 1..N = preguntas · N+1 = resultado + CTA cuenta.
 */
export default function DiagnosticPublic() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = 'Priviax · Diagnóstico gratis Ley 21.719';
    (async () => {
      try {
        const { data } = await api.get('/public/diagnostic/questions');
        setCategories(data.categories);
        setQuestions(data.questions);
      } catch {
        toast.error('No pudimos cargar las preguntas.');
      } finally { setLoading(false); }
    })();
  }, []);

  const questionMatchesConditions = (q) => {
    if (!q?.show_if) return true;
    for (const [qid, cond] of Object.entries(q.show_if)) {
      const val = answers[qid];
      if (cond.eq && val !== cond.eq) return false;
      if (cond.neq && val === cond.neq) return false;
      if (cond.has_any_except) {
        if (!Array.isArray(val) || val.length === 0) return false;
        if (val.filter((v) => !cond.has_any_except.includes(v)).length === 0) return false;
      }
    }
    return true;
  };

  useEffect(() => {
    const patch = {};
    for (const q of questions) {
      if (q.auto_answer_when_hidden && !questionMatchesConditions(q)) {
        if (answers[q.id] !== q.auto_answer_when_hidden) patch[q.id] = q.auto_answer_when_hidden;
      }
    }
    if (Object.keys(patch).length > 0) setAnswers((p) => ({ ...p, ...patch }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, questions]);

  const visibleQuestions = useMemo(
    () => questions.filter(questionMatchesConditions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, answers],
  );
  const total = visibleQuestions.length;
  const progress = step === 0 ? 0 : (result ? 100 : Math.round(((step - 1) / total) * 100));
  const currentQ = step > 0 && step <= total ? visibleQuestions[step - 1] : null;
  const answered = currentQ ? answers[currentQ.id] : null;
  const isMulti = currentQ?.type === 'multi_select';

  const setAnswer = (val) => currentQ && setAnswers((p) => ({ ...p, [currentQ.id]: val }));
  const toggleMulti = (val) => {
    if (!currentQ) return;
    const cur = Array.isArray(answers[currentQ.id]) ? answers[currentQ.id] : [];
    let next;
    if (val === 'ninguna') next = cur.includes('ninguna') ? [] : ['ninguna'];
    else next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur.filter((x) => x !== 'ninguna'), val];
    setAnswers((p) => ({ ...p, [currentQ.id]: next }));
  };
  const isValid = () => currentQ && (isMulti ? Array.isArray(answered) && answered.length > 0 : !!answered);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })),
      };
      const { data } = await api.post('/public/diagnostic/submit', payload);
      setResult(data);
    } catch {
      toast.error('No pudimos calcular el diagnóstico. Intente de nuevo.');
    } finally { setSubmitting(false); }
  };

  const goNext = () => {
    if (step === 0) { setStep(1); return; }
    if (step < total) { setStep(step + 1); return; }
    submit();
  };
  const goBack = () => setStep(Math.max(0, step - 1));

  if (loading) return <FullPageMsg>Cargando cuestionario…</FullPageMsg>;

  // ─────────── RESULT
  if (result) {
    const meta = SCORE_LEVELS[result.level] || SCORE_LEVELS.critico;
    return (
      <Shell>
        <div className="max-w-4xl mx-auto py-10 px-4 space-y-8 animate-fade-in" data-testid="public-diagnostic-result">
          <div className="border border-[#E5E7EB] rounded-md bg-white p-8 md:p-10 grid md:grid-cols-2 gap-8 items-center shadow-sm">
            <div>
              <div className="pv-eyebrow mb-2">Su resultado</div>
              <h2 className="text-2xl md:text-3xl font-semibold text-[#1C2329] tracking-tight">
                Su score inicial es{' '}
                <span style={{ color: meta.color }}>{result.score}/100</span>
              </h2>
              <p className="text-sm text-[#374151] mt-3 leading-relaxed whitespace-pre-line">
                {result.ai_summary}
              </p>
            </div>
            <div className="flex justify-center">
              <ScoreGauge score={result.score} level={result.level} size={240} />
            </div>
          </div>

          {/* CTA registro */}
          <div className="border border-[#2A9B68] rounded-md bg-[#F0F7F5] p-6 md:p-8">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-[#143A34] text-white flex items-center justify-center shrink-0">
                <Sparkles size={18} strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="text-[18px] font-semibold text-[#0F2A26]">
                  Ya invirtió 15 minutos. No pierda lo avanzado.
                </h3>
                <p className="text-[13.5px] text-[#374151] mt-1 leading-relaxed">
                  Cree su cuenta gratuita para <b>guardar este resultado</b>, ver el <b>plan
                  de acción priorizado</b> con las tareas concretas para subir su score, y volver
                  cuando quiera para medir su avance. Sin tarjeta de crédito.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[13px] text-[#065F46] mb-5">
              <div className="flex items-center gap-1.5"><Check size={13} className="shrink-0" /> Guarda su score y respuestas</div>
              <div className="flex items-center gap-1.5"><Check size={13} className="shrink-0" /> Plan de acción concreto</div>
              <div className="flex items-center gap-1.5"><Check size={13} className="shrink-0" /> Vuelva a medir cuando quiera</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                to={`/registro?diag=${result.id}`}
                className="flex-1 pv-btn-primary flex items-center justify-center gap-2"
                data-testid="public-result-register"
              >
                Crear cuenta <ArrowRight size={14} />
              </Link>
              <Link
                to={`/login?diag=${result.id}`}
                className="hidden"
                //className="flex-1 pv-btn-secondary flex items-center justify-center gap-2"
                data-testid="public-result-login"
              >
                Ya tengo cuenta
              </Link>
            </div>
          </div>

          <div className="text-center">
            <Link to="/" className="text-[12.5px] text-[#6B7280] hover:text-[#143A34]">
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  // ─────────── INTRO (sinceridad)
  if (step === 0) {
    return (
      <Shell>
        <div className="max-w-3xl mx-auto py-10 px-4 animate-fade-in">
          <div className="border border-[#E5E7EB] rounded-md bg-white p-8 md:p-12 shadow-sm">
            <div className="w-12 h-12 rounded-md bg-[#ECFDF5] border border-[#2A9B68]/30 flex items-center justify-center text-[#2A9B68]">
              <ClipboardList size={20} strokeWidth={1.5} />
            </div>
            <h2 className="mt-5 text-2xl md:text-3xl font-semibold text-[#1C2329] tracking-tight">
              ¿Está su empresa preparada para la Ley 21.719?
            </h2>
            <p className="mt-3 text-[15px] text-[#374151] leading-relaxed">
              Le vamos a hacer {total} preguntas simples sobre cómo su empresa trata datos
              personales hoy. Sin registrarse, sin tarjeta. Al final va a ver su
              <b> score 0–100</b> de cumplimiento.
            </p>

            <div className="mt-6 border-l-4 border-[#F59E0B] bg-[#FFFBEB] px-4 py-3">
              <div className="flex items-start gap-2">
                <Shield size={16} className="text-[#B45309] shrink-0 mt-0.5" strokeWidth={2} />
                <div className="text-[13.5px] text-[#78350F] leading-relaxed">
                  <b>Un consejo antes de empezar:</b> responda con total sinceridad.
                  Si tiene dudas sobre una pregunta, marque <b>«No»</b> o <b>«No lo sé»</b>. La
                  idea del diagnóstico es identificar lo que le falta para poder revisarlo
                  después con tranquilidad — no es un examen. Cuanto más honesto sea, más útil
                  será el plan que le vamos a preparar.
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { k: 'Tiempo', v: '10–15 min' },
                { k: 'Preguntas', v: `${total} en 8 categorías` },
                { k: 'Costo', v: 'Gratis · sin cuenta' },
              ].map((it) => (
                <div key={it.k} className="p-4 border border-[#E5E7EB] rounded-md">
                  <div className="pv-eyebrow">{it.k}</div>
                  <div className="mt-1 text-[15px] font-medium text-[#1C2329]">{it.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Link to="/" className="pv-btn-secondary flex items-center gap-2" data-testid="public-diag-cancel">
                <ArrowLeft size={14} /> Volver
              </Link>
              <button
                className="pv-btn-primary flex items-center gap-2"
                onClick={() => setStep(1)}
                data-testid="public-diag-start"
              >
                Comenzar diagnóstico <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ─────────── QUESTION
  const catMeta = categories.find((c) => c.id === currentQ?.category);
  return (
    <Shell>
      <div className="max-w-3xl mx-auto py-8 px-4 animate-fade-in">
        <div className="flex items-center justify-between text-[12px] text-[#6B7280] mb-3">
          <div className="uppercase tracking-wider">Pregunta {step} de {total}</div>
          <div className="tabular-nums">{progress}% completado</div>
        </div>
        <div className="pv-seg-bar mb-8"><span style={{ width: `${progress}%` }} /></div>
        <div className="border border-[#E5E7EB] rounded-md bg-white p-8 md:p-10 shadow-sm" data-testid={`public-question-${currentQ.id}`}>
          <div className="pv-eyebrow" style={{ color: '#2A9B68' }}>{catMeta?.label}</div>
          <h2 className="mt-3 text-xl md:text-2xl font-semibold text-[#1C2329] tracking-tight leading-snug">
            {withTermTooltips(currentQ.prompt)}
          </h2>
          {currentQ.help && (
            <div className="mt-3 flex items-start gap-2 text-[13px] text-[#6B7280]">
              <HelpCircle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span className="whitespace-pre-line leading-relaxed">{withTermTooltips(currentQ.help)}</span>
            </div>
          )}
          {currentQ.deep_dive && (
            <details className="mt-4 group border border-[#E5E7EB] bg-[#FAFBFC]">
              <summary className="cursor-pointer select-none flex items-center gap-2 px-3 py-2 text-[12.5px] font-medium text-[#0F2A26] hover:bg-[#F0F7F5] list-none">
                <Scale size={13} strokeWidth={1.75} className="text-[#065F46]" />
                <span className="flex-1">{currentQ.deep_dive.title}</span>
                <ChevronDown size={13} className="transition-transform group-open:rotate-180 text-[#6B7280]" />
              </summary>
              <div className="px-3 py-3 border-t border-[#E5E7EB] space-y-3 text-[12.5px] text-[#374151] leading-relaxed">
                {currentQ.deep_dive.sections.map((s, i) => (
                  <div key={i}>
                    <div className="font-semibold text-[#0F2A26] mb-0.5 text-[12px] uppercase tracking-wider">{s.heading}</div>
                    <div className="whitespace-pre-line">{withTermTooltips(s.body)}</div>
                  </div>
                ))}
              </div>
            </details>
          )}

          <div className="mt-8 space-y-3">
            {isMulti
              ? currentQ.options.map((opt) => {
                  const checked = Array.isArray(answered) && answered.includes(opt.value);
                  const isNinguna = opt.value === 'ninguna';
                  return (
                    <button key={opt.value} type="button" onClick={() => toggleMulti(opt.value)}
                      className={`w-full flex items-start justify-between px-4 py-3 rounded-md border text-left transition-colors ${checked ? 'border-[#2A9B68] bg-[#ECFDF5]' : 'border-[#E5E7EB] bg-white hover:bg-[#FAFBFC]'} ${isNinguna ? 'border-dashed' : ''}`}
                      data-testid={`public-option-${currentQ.id}-${opt.value}`}>
                      <span className={`text-[14px] leading-relaxed pr-3 ${checked ? 'text-[#065F46]' : 'text-[#1C2329]'} ${isNinguna ? 'italic' : ''}`}>{opt.label}</span>
                      <span className={`w-5 h-5 mt-0.5 shrink-0 rounded border flex items-center justify-center ${checked ? 'bg-[#2A9B68] border-[#2A9B68]' : 'border-[#D1D5DB]'}`}>
                        {checked && <Check size={12} className="text-white" strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })
              : currentQ.options.map((opt) => {
                  const selected = answered === opt.value;
                  return (
                    <button key={opt.value} type="button" onClick={() => setAnswer(opt.value)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-md border text-left transition-colors ${selected ? 'border-[#2A9B68] bg-[#ECFDF5]' : 'border-[#E5E7EB] bg-white hover:bg-[#FAFBFC]'}`}
                      data-testid={`public-option-${currentQ.id}-${opt.value}`}>
                      <span className={`text-[15px] font-medium ${selected ? 'text-[#065F46]' : 'text-[#1C2329]'}`}>{opt.label}</span>
                      <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${selected ? 'bg-[#2A9B68] border-[#2A9B68]' : 'border-[#D1D5DB]'}`}>
                        {selected && <Check size={12} className="text-white" strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })}
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between">
          <button className="pv-btn-secondary flex items-center gap-2" onClick={goBack} data-testid="public-diag-back">
            <ArrowLeft size={14} /> Anterior
          </button>
          <button className="pv-btn-primary flex items-center gap-2" disabled={!isValid() || submitting} onClick={goNext} data-testid="public-diag-next">
            {step === total ? (submitting ? 'Calculando…' : 'Ver mi score') : 'Siguiente'}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <header className="border-b border-[#E5E7EB] bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><LogoFull height={22} /></Link>
          <Link to="/login" className="text-[12.5px] text-[#374151] hover:text-[#143A34]">
            ¿Ya tiene cuenta? Ingresar
          </Link>
        </div>
      </header>
      {children}
    </div>
  );
}

function FullPageMsg({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-sm text-[#6B7280]">{children}</div>;
}
